import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Phone, MessageSquare, CheckCircle, AlertTriangle, Wrench, Clock, Shield } from 'lucide-react';
import { Button } from '../components/ui/button';
import { businessInfo } from '../data/mock';

const SpringsPage = () => {
  const springTypes = [
    {
      title: "Torsion Springs",
      description: "Mounted above the garage door opening, providing balanced lifting force for smooth operation.",
      features: ["Long-lasting durability", "Smooth lifting action", "Better balance control", "Quieter operation"]
    },
    {
      title: "Extension Springs",
      description: "Installed along the sides of the door, stretching to provide lifting power.",
      features: ["Cost-effective solution", "Easier to replace", "Works with safety cables", "Suitable for lighter doors"]
    }
  ];

  const warningSigns = [
    "Door feels heavier than usual",
    "Visible gap in the spring",
    "Door opens unevenly or crooked",
    "Loud bang or snapping sound",
    "Door won't stay open",
    "Garage door opener straining"
  ];

  const faqItems = [
    {
      q: "How long do garage door springs last?",
      a: "Most garage door springs last 7-10 years or 10,000-15,000 cycles. With proper maintenance, high-cycle springs can last even longer for Saskatoon homeowners."
    },
    {
      q: "Can I replace garage door springs myself?",
      a: "We strongly advise against DIY spring replacement. Springs are under extreme tension and can cause serious injury. Professional installation ensures safety for Saskatoon families."
    },
    {
      q: "Why do both springs need replacing?",
      a: "If one spring breaks, the other is typically at the same wear level. Replacing both prevents another breakdown soon after and ensures balanced operation for Saskatoon homes."
    },
    {
      q: "What causes garage door springs to break?",
      a: "Normal wear and tear, temperature changes in Saskatoon winters, lack of lubrication, and improper spring size all contribute to spring failure."
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50">
      {/* Hero */}
      <section className="pt-32 pb-12 bg-gradient-to-r from-blue-900 to-blue-800">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center text-white">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Garage Door Springs Saskatoon
            </h1>
            <p className="text-xl text-blue-100 mb-4">
              Professional Spring Replacement & Repair in Saskatoon
            </p>
            <p className="text-lg text-blue-200 mb-8">
              Fast, safe spring service for Saskatoon homeowners. Restore your garage door's smooth operation today.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button 
                onClick={() => window.location.href = `tel:${businessInfo.phone}`}
                size="lg"
                className="bg-green-600 hover:bg-green-700 text-white font-bold px-8 py-6 text-lg"
              >
                <Phone size={20} className="mr-2" />
                Call {businessInfo.phone}
              </Button>
              <Button 
                onClick={() => window.location.href = '/quote'}
                size="lg"
                variant="outline"
                className="border-2 border-white text-white hover:bg-white hover:text-blue-900 font-bold px-8 py-6 text-lg"
              >
                <MessageSquare size={20} className="mr-2" />
                Get Free Quote
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Problem Statement */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="bg-red-50 border-l-4 border-red-600 p-6 mb-8">
              <div className="flex items-start gap-3">
                <AlertTriangle size={32} className="text-red-600 flex-shrink-0" />
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">Broken Spring? You're Not Alone</h2>
                  <p className="text-gray-700 mb-3">
                    A garage door problem can change your whole day in Saskatoon. One minute everything feels normal, then suddenly the door won't open, closes unevenly, or makes a loud sound. <strong>306 Garage Doors</strong> helps Saskatoon homeowners solve spring issues with reliable service that restores safety and peace of mind.
                  </p>
                  <p className="text-gray-700">
                    Garage door springs carry the weight every time your door opens or closes in Saskatoon. When that balance starts slipping, homeowners may notice rough motion, extra noise, or a door that feels risky. We treat springs as a core service because spring trouble affects your daily routine and home security.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Warning Signs */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Warning Signs Your Springs Need Attention</h2>
              <p className="text-xl text-gray-600">Saskatoon homeowners should watch for these common symptoms</p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {warningSigns.map((sign, idx) => (
                <Card key={idx} className="border-2 border-orange-200">
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <AlertTriangle size={20} className="text-orange-600 flex-shrink-0 mt-1" />
                      <p className="text-gray-800 font-semibold">{sign}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Spring Types */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Types of Garage Door Springs</h2>
              <p className="text-xl text-gray-600">We service all spring systems in Saskatoon</p>
            </div>
            <div className="grid md:grid-cols-2 gap-8">
              {springTypes.map((type, idx) => (
                <Card key={idx} className="border-2 hover:border-blue-600 hover:shadow-xl transition-all">
                  <CardHeader>
                    <CardTitle className="text-2xl text-blue-800">{type.title}</CardTitle>
                    <p className="text-gray-600 mt-2">{type.description}</p>
                  </CardHeader>
                  <CardContent>
                    <h4 className="font-bold text-gray-900 mb-3">Benefits:</h4>
                    <ul className="space-y-2">
                      {type.features.map((feature, fidx) => (
                        <li key={fidx} className="flex items-start gap-2">
                          <CheckCircle size={18} className="text-green-600 flex-shrink-0 mt-0.5" />
                          <span className="text-gray-700">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Why Professional Service */}
      <section className="py-16 bg-gradient-to-br from-blue-50 to-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">Why Choose Professional Spring Service in Saskatoon?</h2>
            <div className="grid md:grid-cols-3 gap-6 mt-8">
              <Card>
                <CardHeader>
                  <Shield size={48} className="text-blue-700 mx-auto mb-3" />
                  <CardTitle>Safety First</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600">Springs under high tension require professional tools and expertise to replace safely</p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader>
                  <Wrench size={48} className="text-blue-700 mx-auto mb-3" />
                  <CardTitle>Right Equipment</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600">We use professional winding bars and calibrated tools for precise spring installation</p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader>
                  <Clock size={48} className="text-blue-700 mx-auto mb-3" />
                  <CardTitle>Fast Service</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600">Same-day service available for Saskatoon homeowners who need urgent spring replacement</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 text-center mb-12">Frequently Asked Questions</h2>
            <div className="space-y-6">
              {faqItems.map((item, idx) => (
                <Card key={idx} className="border-2">
                  <CardHeader>
                    <CardTitle className="text-lg text-gray-900">{item.q}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-700">{item.a}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-r from-green-600 to-green-700">
        <div className="container mx-auto px-4 text-center text-white">
          <h2 className="text-4xl font-bold mb-6">Need Spring Replacement in Saskatoon?</h2>
          <p className="text-xl mb-8 max-w-3xl mx-auto">
            Don't risk your safety with DIY spring replacement. Our expert technicians serve Saskatoon with fast, professional service.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button 
              onClick={() => window.location.href = `tel:${businessInfo.phone}`}
              size="lg"
              className="bg-white text-green-700 hover:bg-gray-100 font-bold px-12 py-8 text-xl"
            >
              <Phone size={24} className="mr-2" />
              Call {businessInfo.phone}
            </Button>
            <Button 
              onClick={() => window.location.href = '/quote'}
              size="lg"
              variant="outline"
              className="border-2 border-white text-white hover:bg-white hover:text-green-700 font-bold px-12 py-8 text-xl"
            >
              Request Free Quote
            </Button>
          </div>
          <p className="mt-6 text-green-100 italic">{businessInfo.confidentialMessage}</p>
        </div>
      </section>
    </div>
  );
};

export default SpringsPage;
